import { applySpec, compose, curry, find, map, path, pick, prop } from 'ramda';
import { dotPathEq, dotPathOr } from 'ramda-extension';

import { getEnvironment } from '../utils.mjs';
import { wbAssetImageUrl } from '../wb-asset/index.js';

const { BASE_CONTENTFUL_IMAGE_URL } = getEnvironment();

export const selectFields = prop('fields');
export const selectFieldsByKey = (key) => compose(selectFields, prop(key));

export const selectField = curry((field, obj) =>
  compose(prop(field), selectFields)(obj)
);

// Note: Cloudinary was set to automatically download images starting with `hub/content`
// from `images.ctfassets.net/5nfc2z61o7ux`. This means that all the images the from
// Contentful `hubTemplate` JSON are already copied to asset service and we just need
// to keep referencing them by the same Cloudinary url.
export const extractPublicId = (url) => {
  if (!url) return '';
  url = url.replace(`//${BASE_CONTENTFUL_IMAGE_URL}`, 'hub/content');
  url = url.replace(BASE_CONTENTFUL_IMAGE_URL, 'hub/content');

  return url;
};

export const getWbAssetUrl = (url) => {
  if (!url) return url;

  return url.match('videos.ctfassets.net')
    ? wbAssetImageUrl({ publicId: extractPublicId(url) })
    : wbAssetImageUrl({ publicId: extractPublicId(url), imageType: 'image' });
};

const selectUrl = path(['file', 'url']);
// Note: Since WBRT-5443 we have stopped hosting images on Contenful so we must use asset service.
export const selectAssetUrl = compose(getWbAssetUrl, selectUrl, selectFields);
const selectAssetPublicId = compose(extractPublicId, selectUrl);
// export const selectAssetUrlByKey = (key) => compose(selectAssetUrl, prop(key)); // not in use
export const selectMediaFromList = curry((list, title) =>
  find(dotPathEq('fields.title', title), list)
);

export const selectContentType = path(['sys', 'contentType', 'sys', 'id']);
export const selectEntryId = path(['sys', 'id']);

export const flattenFields = map(selectFields);

export const flattenImageAsset = compose(
  (fields) =>
    fields
      ? {
          image: {
            publicId: selectAssetPublicId(fields),
          },
          alt: fields.description,
        }
      : null,
  selectFields
);

/**
 * Given a responsive video type from Cloudinary, extracts the file object from
 * the mobile and desktop video fields.
 *
 * @param {object} responsiveVideo - The responsive video object from
 *   cloudinary.
 * @returns {object} - The flattened resonsive video object which is suitable
 *   for use in a ResponsiveVideo component.
 */
export const flattenResponsiveVideo = applySpec({
  desktopVideo: dotPathOr({}, 'fields.desktopVideo.fields.file'),
  mobileVideo: dotPathOr({}, 'fields.mobileVideo.fields.file'),
});

/**
 * Given a responsive hero image type from Cloudinary, extracts the file object
 * from the mobile and desktop video fields.
 *
 * @param {object} responsiveHeroImage - The responsive video object from
 *   cloudinary.
 * @returns {object} - The flattened hero image which is suitable for use in
 *   a CloudinaryImage component.
 */
export const flattenResponsiveHeroImage = applySpec({
  image: { publicId: compose(selectAssetPublicId, selectFields) },
});

export const flattenResponsiveImage = compose((fields) => {
  if (!fields) return null;

  return {
    // migration to cloudinary removes the need for both versions
    // this will eventually be removed in favor of a single field
    image: {
      publicId: compose(selectAssetPublicId, selectFields)(fields.regular),
    },
    alt: fields.alt,
  };
}, selectFields);

/**
 * Given an array of values and a cloudinary object, picks the fields from the
 * cloudinary object as determined by the array of values.
 *
 * @param {Array<string>} values - The names of the keys to extract from the
 *   cloudinary object.
 * @param {object} object - The cloudinary object to extract the fields from.
 * @returns {object} - An object of the selected key/value pairs.
 */
export const selectFieldValues = curry((values, object) =>
  compose(pick(values), selectFields)(object)
);
